// src/components/dashboard/admin/AdminOrganisations.tsx
// ✅ ALIGNED: Dedicated page for viewing and managing all organisations
import React from 'react';
import { useNavigate } from "react-router-dom";
import { useAdmin } from '@/contexts/AdminContext';
import { OrgLogo } from '@/components/custom-ui/OrgLogo';
import { AdminList, AdminListRow } from './AdminList';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Building2,
    Plus,
    RefreshCw,
    ArrowRight,
    Search,
} from 'lucide-react';
import { Input } from '@/components/ui/input';

// Import Nexilink components
import {
    ActionButton,
    OutlineButton,
    EmptyState,
    IconChip,
    StatusBadge,
} from '@/components/custom-ui';

const AdminOrganisations: React.FC = () => {
    const navigate = useNavigate();
    const {
        organisations,
        isLoadingOrganisations,
        organisationsError,
        refreshDashboard,
    } = useAdmin();

    const [searchQuery, setSearchQuery] = React.useState('');

    // Filter organisations based on search
    const filteredOrganisations = organisations.filter(org =>
        org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        org.city?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-6">
            {/* Title is in the top header strip (TopHeader). #358 — every control
                for this collection now lives in the AdminList header, so the three
                admin lists share one control strip instead of three arrangements. */}
            <AdminList
                title="Organisationer"
                count={filteredOrganisations.length}
                total={organisations.length}
                isLoading={isLoadingOrganisations}
                // #353 — same swallow as Användare had; a failed load used to be
                // indistinguishable from "no organisations yet".
                error={organisationsError}
                onRetry={() => refreshDashboard()}
                search={{
                    value: searchQuery,
                    onChange: setSearchQuery,
                    placeholder: 'Sök namn, stad…',
                }}
                action={
                    <div className="flex gap-2">
                        <OutlineButton onClick={() => refreshDashboard()} disabled={isLoadingOrganisations} icon={RefreshCw}>
                            Uppdatera
                        </OutlineButton>
                        <ActionButton onClick={() => navigate('/dashboard/organisations/new')} icon={Plus}>
                            Ny organisation
                        </ActionButton>
                    </div>
                }
                empty={
                    searchQuery ? (
                        <EmptyState
                            icon={Search}
                            title="Inga träffar"
                            description={`Inga organisationer matchar "${searchQuery}".`}
                            action={{ label: 'Rensa sökning', onClick: () => setSearchQuery('') }}
                        />
                    ) : (
                        <EmptyState
                            icon={Building2}
                            title="Inga organisationer ännu"
                            description="En organisation är kunden. Enheter och arbetsgivare skapas inuti den."
                            action={{
                                label: 'Ny organisation',
                                onClick: () => navigate('/dashboard/organisations/new'),
                                icon: Plus,
                            }}
                        />
                    )
                }
            >
                {filteredOrganisations.map((org) => (
                    <AdminListRow
                        key={org.id}
                        onClick={() => navigate(`/dashboard/organisations/${org.id}`)}
                        /* #354 — a row here IS a specific organisation, so it shows that
                           organisation. Building2 stays the glyph for the CONCEPT
                           (nav card, empty state); it is not an identity. */
                        leading={<OrgLogo name={org.name} logoUrl={org.logo_url} size="md" />}
                        title={org.name}
                        subtitle={
                            <>
                                {org.units_count} enheter · {org.employers_count} arbetsgivare
                                {org.city && ` · ${org.city}`}
                            </>
                        }
                        trailing={
                            <>
                                {!org.is_active && <StatusBadge status="inactive" label="Inaktiv" />}
                                <OutlineButton
                                    size="sm"
                                    onClick={() => navigate(`/dashboard/organisations/${org.id}`)}
                                    icon={ArrowRight}
                                    iconPosition="right"
                                >
                                    Visa detaljer
                                </OutlineButton>
                            </>
                        }
                    />
                ))}
            </AdminList>
        </div>
    );
};

export default AdminOrganisations;